<script setup lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field, block, addItem, removeItem, updateItem } = useInlineBlockEditor(props.id);

const type = field<string>("type", "single");
const defaultValue = field<string>("defaultValue", "");
const highlightActive = field<boolean>("highlightActive", false);
const items = computed<any[]>(() => block.value?.children ?? []);

const addAccordionItem = () => {
  const newId = crypto.randomUUID();
  addItem("children", {
    id: newId,
    title: t("defaultItemTitle"),
    componentId: ComponentDefinitionIds.ACCORDION_ITEM,
    highlightActive: null,
    children: [],
  });
  if (type.value === "single" && !defaultValue.value) defaultValue.value = newId;
};

const removeAccordionItem = (index: number) => {
  const removed = items.value[index];
  const nextItems = items.value.filter((_, itemIndex) => itemIndex !== index);
  removeItem("children", index);
  if (removed?.id === defaultValue.value) {
    defaultValue.value = nextItems[0]?.id ?? "";
  }
};
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:panel-top-open" accent="container">
    <EditorField :label="t('typeLabel')">
      <Select :model-value="type" @update:model-value="(value) => (type = value as string)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="single">{{ t("typeSingle") }}</SelectItem>
          <SelectItem value="multiple">{{ t("typeMultiple") }}</SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField v-if="type === 'single'" :label="t('defaultOpenLabel')">
      <Select :model-value="defaultValue" @update:model-value="(value) => (defaultValue = value as string)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue :placeholder="t('defaultOpenPlaceholder')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="item in items" :key="item.id" :value="item.id">
            {{ item.title || t("untitledItem") }}
          </SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('highlightActiveLabel')">
      <div class="flex items-center justify-between">
        <span class="text-[12.5px] text-muted-foreground">{{ t("highlightActiveHint") }}</span>
        <Switch v-model="highlightActive" />
      </div>
    </EditorField>

    <EditorField :label="t('itemsLabel')" :hint="t('itemsHint')">
      <div class="space-y-2">
        <div v-for="(item, index) in items" :key="item.id ?? index" class="flex items-center gap-2">
          <Input
            :model-value="item.title"
            class="h-8 flex-1 rounded-sm bg-muted text-[13px]"
            :placeholder="t('itemTitlePlaceholder')"
            @update:model-value="(value) => updateItem('children', index, { title: value })"
          />
          <Button variant="ghost" size="icon" class="size-8 shrink-0 text-destructive" @click="removeAccordionItem(index)">
            <Icon mode="svg" size="16" name="lucide:x" />
          </Button>
        </div>
      </div>
    </EditorField>

    <Button variant="outline" size="sm" class="self-start rounded-sm text-[13px]" @click="addAccordionItem">
      <Icon mode="svg" size="16" name="lucide:plus" class="mr-1.5" />
      {{ t("addItem") }}
    </Button>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Accordion"
  typeLabel: "Accordion Type"
  typeSingle: "Single"
  typeMultiple: "Multiple"
  defaultOpenLabel: "Default Open Item"
  defaultOpenPlaceholder: "Select an item"
  highlightActiveLabel: "Highlight Active"
  highlightActiveHint: "Emphasize the currently open item."
  itemsLabel: "Accordion Items"
  itemsHint: "Use the main editor to add blocks inside each accordion item."
  itemTitlePlaceholder: "Accordion item title"
  untitledItem: "Untitled item"
  defaultItemTitle: "New Accordion Item"
  addItem: "Add Accordion Item"
</i18n>
